import type {
  ReportGrain,
  ReportMeasureKind,
  ReportParts,
  ReportPeriod,
  ReportQuery,
  ReportQueryResult,
  ReportRow
} from "@gettactic/api";

/** The key of the row that stands for everything past a cut. */
export const OTHER_KEY = "__other__";

/** Series colours in a fixed order; a member keeps its colour whatever is hidden. */
export const SERIES_COLORS = [
  "#2a78d6",
  "#e6a23c",
  "#6f4fc7",
  "#1a9a3c",
  "#d94b3b",
  "#0f9aa8",
  "#c2578a",
  "#8c6d3f"
];
export const OTHER_COLOR = "#b5bcc4";

export const seriesColor = (index: number, isOther: boolean): string =>
  isOther ? OTHER_COLOR : SERIES_COLORS[index % SERIES_COLORS.length];

export type FoldedRow = {
  /** Unique within the result: every key of the row, joined. */
  key: string;
  /** The row's member ids, one per breakdown, for drilling. */
  keys: string[];
  label: string;
  value: number;
  previous: number | null;
  parts: ReportParts | null;
  /** True for the row that folds the rows past the cut; `folded` says how many. */
  isOther: boolean;
  folded: number;
};

type Cell = {
  value: number;
  previous: number | null;
  parts: ReportParts | null;
};

const addParts = (
  a: ReportParts | null,
  b: ReportParts | null
): ReportParts | null =>
  a && b
    ? {
        numerator: a.numerator + b.numerator,
        denominator: a.denominator + b.denominator
      }
    : null;

/**
 * Cells summed into one: a count adds up, a ratio is recomputed from the
 * summed parts. A single cell is itself, whatever the measure. Null when
 * several cannot honestly be one (a ratio without its parts, a duration).
 */
export const combineCells = (
  cells: Cell[],
  kind: ReportMeasureKind
): Cell | null => {
  if (cells.length === 0) {
    return null;
  }
  if (cells.length === 1) {
    return cells[0];
  }
  if (kind === "count") {
    return {
      value: cells.reduce((sum, c) => sum + c.value, 0),
      previous: cells.every((c) => c.previous !== null)
        ? cells.reduce((sum, c) => sum + (c.previous ?? 0), 0)
        : null,
      parts: null
    };
  }
  if (kind === "percent") {
    const parts = cells.reduce<ReportParts | null>(
      (acc, c) => addParts(acc, c.parts),
      { numerator: 0, denominator: 0 }
    );
    if (!parts || parts.denominator === 0) {
      return null;
    }
    return {
      value: parts.numerator / parts.denominator,
      previous: null,
      parts
    };
  }
  return null;
};

// A row of two breakdowns drawn as one list ("Closed · Facilities") keeps
// both keys, so two rows never share a key and a drill pins both members.
const toFolded = (row: ReportRow): FoldedRow => ({
  key: row.keys.join("|"),
  keys: row.keys,
  label:
    row.labels.map((l, i) => l || row.keys[i] || "").join(" · ") ||
    row.keys.join(" · "),
  value: row.value,
  previous: row.previous,
  parts: row.parts,
  isOther: false,
  folded: 0
});

/**
 * The first n rows, with the rest folded into one "Other" row when they can
 * be summed. Folding a single row helps nobody, so n + 1 rows stay as they
 * are; rows that cannot be summed are cut, and the caller says how many.
 */
export const foldRows = (
  rows: ReportRow[],
  n: number,
  kind: ReportMeasureKind
): FoldedRow[] => {
  if (rows.length <= n + 1) {
    return rows.map(toFolded);
  }
  const kept = rows.slice(0, n).map(toFolded);
  const rest = rows.slice(n);
  const other = combineCells(rest, kind);
  if (!other) {
    return kept;
  }
  return [
    ...kept,
    {
      key: OTHER_KEY,
      keys: [],
      label: `Other (${rest.length} more)`,
      ...other,
      isOther: true,
      folded: rest.length
    }
  ];
};

/**
 * Whether an answer is the question's own shape: as many dimensions as the
 * question has breakdowns. While a changed question loads, the previous
 * answer stays on screen, but one of another shape would draw wrong.
 */
export const fitsQuery = (
  result: Pick<ReportQueryResult, "columns"> | undefined,
  query: Pick<ReportQuery, "breakdown">
): boolean =>
  !!result &&
  result.columns.filter((c) => c.type === "dimension").length ===
    (query.breakdown?.length ?? 0);

export type RowOrder = "given" | "value" | "label";

/** Rows in the order asked: as the API ranked them, largest first, or A to Z. */
export const sortRows = (rows: ReportRow[], order: RowOrder): ReportRow[] => {
  if (order === "given") {
    return rows;
  }
  return [...rows].sort((a, b) =>
    order === "value"
      ? b.value - a.value
      : (a.labels[0] ?? "").localeCompare(b.labels[0] ?? "", undefined, {
          numeric: true
        })
  );
};

export type StackSeries = { key: string; label: string; isOther: boolean };
export type StackEntry = {
  key: string;
  label: string;
  isOther: boolean;
  values: Record<string, number>;
  parts: Record<string, ReportParts | null>;
};

/**
 * A two-dimension result pivoted for stacked or grouped bars: one entry per
 * member of the first dimension, one series per member of the second, both
 * in the order the rows came. Entries past maxEntries and series past
 * maxSeries fold into "Other" where the measure can be summed.
 */
export const stackData = (
  result: ReportQueryResult,
  maxEntries: number,
  maxSeries: number,
  kind: ReportMeasureKind
): { entries: StackEntry[]; series: StackSeries[] } => {
  const entryKeys: string[] = [];
  const seriesKeys: string[] = [];
  const labels = new Map<string, string>();
  const cells = new Map<string, Cell>();
  for (const row of result.rows) {
    const [entry, series] = row.keys;
    if (entry === undefined || series === undefined) {
      continue;
    }
    if (!labels.has(`e:${entry}`)) {
      entryKeys.push(entry);
      labels.set(`e:${entry}`, row.labels[0] ?? entry);
    }
    if (!labels.has(`s:${series}`)) {
      seriesKeys.push(series);
      labels.set(`s:${series}`, row.labels[1] ?? series);
    }
    cells.set(`${entry}|${series}`, {
      value: row.value,
      previous: row.previous,
      parts: row.parts
    });
  }

  const fold = (keys: string[], max: number) =>
    keys.length > max + 1
      ? { kept: keys.slice(0, max), folded: keys.slice(max) }
      : { kept: keys, folded: [] };
  const entryCut = fold(entryKeys, maxEntries);
  const seriesCut = fold(seriesKeys, maxSeries);

  const cellsOf = (entries: string[], series: string[]): Cell[] =>
    entries.flatMap((e) =>
      series.flatMap((s) => {
        const cell = cells.get(`${e}|${s}`);
        return cell ? [cell] : [];
      })
    );

  const series: StackSeries[] = seriesCut.kept.map((key) => ({
    key,
    label: labels.get(`s:${key}`) ?? key,
    isOther: false
  }));
  if (seriesCut.folded.length > 0) {
    series.push({
      key: OTHER_KEY,
      label: `Other (${seriesCut.folded.length} more)`,
      isOther: true
    });
  }
  const seriesMembers = (s: StackSeries): string[] =>
    s.isOther ? seriesCut.folded : [s.key];

  const entryOf = (
    key: string,
    label: string,
    members: string[],
    isOther: boolean
  ): StackEntry => {
    const values: Record<string, number> = {};
    const parts: Record<string, ReportParts | null> = {};
    for (const s of series) {
      const cell = combineCells(cellsOf(members, seriesMembers(s)), kind);
      values[s.key] = cell?.value ?? 0;
      parts[s.key] = cell?.parts ?? null;
    }
    return { key, label, isOther, values, parts };
  };

  const entries = entryCut.kept.map((key) =>
    entryOf(key, labels.get(`e:${key}`) ?? key, [key], false)
  );
  if (entryCut.folded.length > 0) {
    entries.push(
      entryOf(
        OTHER_KEY,
        `Other (${entryCut.folded.length} more)`,
        entryCut.folded,
        true
      )
    );
  }
  return { entries, series };
};

const day = (iso: string): Date => new Date(`${iso}T00:00:00Z`);
const iso = (date: Date): string => date.toISOString().slice(0, 10);

/** The last day a bucket covers: the day itself, six days on, or the month's end. */
export const bucketEnd = (bucket: string, grain: ReportGrain): string => {
  const start = day(bucket);
  if (grain === "week") {
    start.setUTCDate(start.getUTCDate() + 6);
    return iso(start);
  }
  if (grain === "month") {
    return iso(
      new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + 1, 0))
    );
  }
  return bucket;
};

/**
 * The custom period a drag across buckets selects: from the first bucket's
 * day to the last day of the last bucket, never past the period's own end.
 */
export const zoomPeriod = (
  buckets: string[],
  fromIndex: number,
  toIndex: number,
  grain: ReportGrain,
  periodTo: string
): ReportPeriod | null => {
  const [a, b] =
    fromIndex <= toIndex ? [fromIndex, toIndex] : [toIndex, fromIndex];
  const from = buckets[a];
  const last = buckets[b];
  if (!from || !last) {
    return null;
  }
  const end = bucketEnd(last, grain);
  return { preset: "custom", from, to: end > periodTo ? periodTo : end };
};
